<script>
/* global axios */
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { useAlert } from 'dashboard/composables';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export default {
  components: {
    BaseModal,
  },
  props: {
    assignmentId: {
      type: String,
      default: null,
    },
    startDateProp: {
      type: String,
      default: null,
    },
    endDateProp: {
      type: String,
      default: null,
    },
    calendarDay: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      isLoading: false,
      selectedBranch: null,
      selectedStaff: null,
      startDate: null,
      endDate: null,
      timeSlots: [],
      editScope: 'all', // 'all' or 'selected'
      editType: 'one-time', // 'one-time' or 'permanent' (for schedule edits)
    }
  },
  computed: {
    branchList() {
      const list = this.$store.state.branchV2.allBranches || [];
      return list.map(b => ({
        id: b.branch.id,
        name: b.branch.name,
        raw: b,
      }));
    },

    staffOptions() {
      return this.$store.state.staffOptions.allOptions || [];
    },

    isEditMode() {
      return !!this.assignmentId || !!this.calendarDay;
    },

    assignment() {
      if (!this.assignmentId) return null;
      return this.$store.getters['staffCalendar/assignments'].find(a => a.id === this.assignmentId);
    },

    modalTitle() {
      if (this.calendarDay) {
        return this.getEditTitleForCalendarDay();
      }
      return this.isEditMode ? 'Edit Assignment' : 'Create Assignment';
    },

    selectedBranchName() {
      return (
        this.branchList?.find(b => b.id === this.selectedBranch)?.name || ''
      );
    },

    selectedStaffName() {
      return (
        this.staffOptions?.find(o => o.staff.id === this.selectedStaff)
          ?.staff?.name || ''
      );
    },

    isSpecificAssignment() {
      return this.calendarDay?.source_type === 'specific_assignment';
    },

    isScheduleEdit() {
      return this.calendarDay?.source_type === 'schedule';
    },

    isUnavailabilityEdit() {
      return this.calendarDay?.source_type === 'unavailability';
    },

    isBranchClosed() {
      return this.calendarDay?.source_type === null;
    },

    canEdit() {
      return !this.isBranchClosed;
    },

    showScopeOptions() {
      return this.isSpecificAssignment && this.calendarDay?.assignment_date_range;
    },

    showEditTypeOptions() {
      return this.isScheduleEdit;
    },

    assignmentDateRange() {
      return this.calendarDay?.assignment_date_range;
    },

    // Display staff and branch names for calendar day edits
    calendarStaffName() {
      if (!this.calendarDay) return '';
      const assignment = this.$store.getters['staffCalendar/assignments'].find(a => 
        a.calendarDay && a.calendarDay.date === this.calendarDay.date &&
        a.calendarDay.source_id === this.calendarDay.source_id
      );
      return assignment?.staffName || 'Staff';
    },

    calendarBranchName() {
      if (!this.calendarDay) return '';
      const assignment = this.$store.getters['staffCalendar/assignments'].find(a => 
        a.calendarDay && a.calendarDay.date === this.calendarDay.date &&
        a.calendarDay.source_id === this.calendarDay.source_id
      );
      return assignment?.branchName || 'Branch';
    },

    // For specific assignments editing selected days, restrict date range
    minDate() {
      if (this.isSpecificAssignment && this.editScope === 'selected' && this.assignmentDateRange) {
        return this.assignmentDateRange.start_date;
      }
      return null;
    },

    maxDate() {
      if (this.isSpecificAssignment && this.editScope === 'selected' && this.assignmentDateRange) {
        return this.assignmentDateRange.end_date;
      }
      return null;
    },

    // For permanent schedule edits and specific assignment "all days" edits, dates should be read-only
    areDatesReadOnly() {
      // Permanent schedule edits - dates are read-only (day of week matters, not specific date)
      if (this.isScheduleEdit && this.editType === 'permanent') {
        return true;
      }
      // Specific assignment "all days" edit - dates are immutable (updating in place)
      if (this.isSpecificAssignment && this.editScope === 'all') {
        return true;
      }
      return false;
    },

    // For unavailability edits, start date should be read-only
    isStartDateReadOnly() {
      return this.isUnavailabilityEdit || this.areDatesReadOnly;
    },

    // For unavailability and permanent schedule edits, end date should be read-only
    isEndDateReadOnly() {
      return this.isUnavailabilityEdit || this.areDatesReadOnly;
    },
  },
  watch: {
    assignmentId() {
      this.initializeFormData();
    },
    calendarDay() {
      this.initializeFormData();
    },
    startDateProp(newVal) {
      if (newVal && !this.isEditMode) {
        this.startDate = newVal;
      }
    },
    endDateProp(newVal) {
      if (newVal && !this.isEditMode) {
        this.endDate = newVal;
      }
    },
    editScope(newVal) {
      // Update date range when switching between all/selected for specific assignments
      if (this.isSpecificAssignment && this.assignmentDateRange) {
        if (newVal === 'all') {
          this.startDate = this.assignmentDateRange.start_date;
          this.endDate = this.assignmentDateRange.end_date;
        } else {
          // Reset to single day (the calendar day being edited)
          this.startDate = this.calendarDay.date;
          this.endDate = this.calendarDay.date;
        }
      }
    },
  },
  async mounted() {
    await this.fetchAllBranches();
    await this.fetchStaffOptions();
    this.initializeFormData();
  },
  methods: {
    getEditTitleForCalendarDay() {
      if (this.isSpecificAssignment) {
        return 'Edit Specific Assignment';
      } else if (this.isScheduleEdit) {
        return 'Edit Schedule';
      } else if (this.isUnavailabilityEdit) {
        return 'Edit Unavailability';
      }
      return 'Edit Assignment';
    },

    initializeFormData() {
      if (this.calendarDay) {
        // Initialize from calendar day data
        this.initializeFromCalendarDay();
      } else if (this.isEditMode && this.assignment) {
        // Legacy assignment editing
        this.selectedBranch = this.assignment.branchId;
        this.selectedStaff = this.assignment.staffId;
        this.startDate = this.assignment.startDate;
        this.endDate = this.assignment.endDate;
        this.timeSlots = this.assignment.timeSlots ? [...this.assignment.timeSlots] : [];
      } else {
        // New assignment creation - add default time slot
        if (this.startDateProp) this.startDate = this.startDateProp;
        if (this.endDateProp) this.endDate = this.endDateProp;
        // Initialize with one default time slot for new assignments
        this.timeSlots = [{
          startTime: '09:00',
          endTime: '17:00',
        }];
      }
    },

    initializeFromCalendarDay() {
      const day = this.calendarDay;
      
      // Set basic info from calendar day
      this.startDate = day.date;
      this.endDate = day.date;
      
      // Initialize time slots from calendar day
      if (day.time_slots && day.time_slots.length > 0) {
        this.timeSlots = day.time_slots.map(slot => ({
          startTime: slot.start_time,
          endTime: slot.end_time,
        }));
      } else {
        this.timeSlots = [];
      }

      // For specific assignments, we might want to show the full range
      if (this.isSpecificAssignment && this.assignmentDateRange) {
        if (this.editScope === 'all') {
          this.startDate = this.assignmentDateRange.start_date;
          this.endDate = this.assignmentDateRange.end_date;
        }
      }
    },

    async fetchAllBranches() {
      try {
        await this.$store.dispatch('branchV2/fetchAllBranches');
      } catch(err) {
        useAlert(`Failed to fetch all branches: ${err}`);
        console.error(err);
      }
    },

    async fetchStaffOptions() {
      try {
        await this.$store.dispatch('staffOptions/fetchAllOptions');
      } catch(err) {
        console.error(err);
        useAlert(`Failed to fetch staff options: ${err}`);
      }
    },

    addTimeSlot() {
      // For permanent schedule edits, only allow one time slot
      if (this.isScheduleEdit && this.editType === 'permanent') {
        useAlert('Permanent schedule edits only support one time slot per day');
        return;
      }

      this.timeSlots.push({
        startTime: '09:00',
        endTime: '17:00',
      });
    },

    removeTimeSlot(index) {
      this.timeSlots.splice(index, 1);
    },

    async handleConfirm() {
      if (!this.canEdit) {
        useAlert('This day cannot be edited (branch closed)');
        return;
      }

      // Validation for date ranges
      if (this.isSpecificAssignment && this.editScope === 'selected') {
        if (this.minDate && this.startDate < this.minDate) {
          useAlert(`Start date must be on or after ${this.minDate}`);
          return;
        }
        if (this.maxDate && this.endDate > this.maxDate) {
          useAlert(`End date must be on or before ${this.maxDate}`);
          return;
        }
      }

      // Validation for time slots
      // Time slots are required for all operations except unavailability edits
      if (this.timeSlots.length === 0) {
        if (this.isUnavailabilityEdit) {
          // Unavailability can be all-day (no time slots) or partial-day (with time slots)
          // This is allowed
        } else {
          useAlert('Please add at least one time slot. Staff must have working hours.');
          return;
        }
      }

      // For permanent schedule edits, only one time slot is allowed
      if (this.isScheduleEdit && this.editType === 'permanent' && this.timeSlots.length > 1) {
        useAlert('Permanent schedule edits only support one time slot per day');
        return;
      }

      // Validate time slot values
      for (let i = 0; i < this.timeSlots.length; i++) {
        const slot = this.timeSlots[i];
        if (!slot.startTime || !slot.endTime) {
          useAlert(`Time slot ${i + 1}: Please set both start and end times`);
          return;
        }
        if (slot.startTime >= slot.endTime) {
          useAlert(`Time slot ${i + 1}: Start time must be before end time`);
          return;
        }
      }

      if (this.calendarDay) {
        await this.handleCalendarDayEdit();
      } else {
        await this.handleLegacyAssignmentEdit();
      }
    },

    async handleCalendarDayEdit() {
      try {
        this.isLoading = true;
        const accountId = this.$store.getters.getCurrentAccountId;
        
        if (this.isSpecificAssignment) {
          await this.handleSpecificAssignmentEdit(accountId);
        } else if (this.isScheduleEdit) {
          await this.handleScheduleEdit(accountId);
        } else if (this.isUnavailabilityEdit) {
          await this.handleUnavailabilityEdit(accountId);
        }

        // Refetch calendar data
        const { from, to } = this.$store.state.staffCalendar.currentDateRange;
        if (from && to) {
          await this.$store.dispatch('staffCalendar/fetchCalendarData', { from, to });
        }

        useAlert('Successfully updated');
        this.$emit('close');
      } catch (err) {
        useAlert(`Failed to update: ${err.message}`);
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async handleSpecificAssignmentEdit(accountId) {
      const sourceId = this.calendarDay.source_id;
      const timeSlots = this.timeSlots.map(slot => ({
        start_time: slot.startTime,
        end_time: slot.endTime,
      }));

      if (this.editScope === 'all') {
        // Edit all days in the assignment
        await axios.put(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/${sourceId}`),
          { time_slots: timeSlots }
        );
      } else {
        // Edit only selected days using split endpoint
        await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments/${sourceId}/split`),
          {
            target_start_date: this.startDate,
            target_end_date: this.endDate,
            action: 'replace',
            new_time_slots: timeSlots,
          }
        );
      }
    },

    async handleScheduleEdit(accountId) {
      const sourceId = this.calendarDay.source_id;
      const timeSlots = this.timeSlots.map(slot => ({
        start_time: slot.startTime,
        end_time: slot.endTime,
      }));

      if (this.editType === 'one-time') {
        // Create a specific assignment override
        const staffId = this.getStaffIdFromCalendarDay();
        const branchId = this.getBranchIdFromCalendarDay();

        await axios.post(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/assignments`),
          {
            staff_id: staffId,
            branch_id: branchId,
            start_date: this.startDate,
            end_date: this.endDate,
            time_slots: timeSlots,
          }
        );
      } else {
        // Edit permanent schedule
        await this.updateScheduleWorkingHours(sourceId);
      }
    },

    async updateScheduleWorkingHours(scheduleId) {
      // First get the current working hours using store action
      const workingHours = await this.$store.dispatch('schedules/fetchWorkingHours', scheduleId);
      
      // Find the working hour record for this day of week
      const dayOfWeek = new Date(this.startDate).getDay();
      const pythonDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Python weekday (0=Monday)
      
      const workingHour = workingHours.find(wh => wh.day_of_week === pythonDayOfWeek);
      
      if (!workingHour) {
        throw new Error('Working hour record not found for this day');
      }

      // Update the working hour
      const timeSlot = this.timeSlots[0]; // Assume first time slot for schedule
      if (!timeSlot) {
        throw new Error('No time slot provided');
      }

      await this.$store.dispatch('schedules/editWorkingHours', {
        scheduleId,
        workingHoursId: workingHour.id,
        payload: {
          day_of_week: pythonDayOfWeek,
          start_time: timeSlot.startTime + ':00',
          end_time: timeSlot.endTime + ':00',
        }
      });
    },

    async handleUnavailabilityEdit(accountId) {
      const sourceId = this.calendarDay.source_id;
      const staffId = this.getStaffIdFromCalendarDay();
      
      const payload = {
        unavailable_date: this.startDate,
      };

      // Add time slots if provided (for partial day unavailability)
      if (this.timeSlots.length > 0) {
        const timeSlot = this.timeSlots[0]; // Use first time slot
        payload.start_time = timeSlot.startTime + ':00';
        payload.end_time = timeSlot.endTime + ':00';
      }

      await this.$store.dispatch('staffV2/updateStaffUnavailability', {
        staffId,
        unavailabilityId: sourceId,
        payload
      });
    },

    getStaffIdFromCalendarDay() {
      // Extract staff ID from the assignment that contains this calendar day
      const assignment = this.$store.getters['staffCalendar/assignments'].find(a => 
        a.calendarDay && a.calendarDay.date === this.calendarDay.date &&
        a.calendarDay.source_id === this.calendarDay.source_id
      );
      return assignment?.staffId || this.selectedStaff;
    },

    getBranchIdFromCalendarDay() {
      // Extract branch ID from the assignment that contains this calendar day
      const assignment = this.$store.getters['staffCalendar/assignments'].find(a => 
        a.calendarDay && a.calendarDay.date === this.calendarDay.date &&
        a.calendarDay.source_id === this.calendarDay.source_id
      );
      return assignment?.branchId || this.selectedBranch;
    },

    async handleLegacyAssignmentEdit() {
      if (!this.selectedBranch) {
        useAlert('Please select a branch');
        return;
      }
      if (!this.selectedStaff) {
        useAlert('Please select a staff');
        return;
      }
      if (!this.startDate) {
        useAlert('Please select a start date');
        return;
      }
      if (this.timeSlots.length === 0) {
        useAlert('Please add at least one time slot. Staff must have working hours.');
        return;
      }

      try {
        this.isLoading = true;

        // Convert camelCase to snake_case for API payload
        const payload = {
          branch_id: this.selectedBranch,
          staff_id: this.selectedStaff,
          start_date: this.startDate,
          end_date: this.endDate,
          time_slots: this.timeSlots.map(slot => ({
            start_time: slot.startTime,
            end_time: slot.endTime,
          })),
        };

        if (this.isEditMode) {
          await this.$store.dispatch('assignments/update', {
            id: this.assignmentId,
            payload,
          });
          useAlert('Assignment updated successfully');
        } else {
          const response = await this.$store.dispatch('assignments/create', payload);
          useAlert('Assignment created successfully');
          if (response.branch_warnings && response.branch_warnings.length) {
            const messages = response.branch_warnings.map(
              warning => `• ${warning.date}: ${warning.reason}`
            ).join('\n')

            useAlert(`Branch Warnings:\n${messages}`)
          }
        }

        // Refetch calendar data with the current date range
        const { from, to } = this.$store.state.staffCalendar.currentDateRange;
        if (from && to) {
          await this.$store.dispatch('staffCalendar/fetchCalendarData', { from, to });
        }

        this.$emit('close');
      } catch (err) {
        useAlert(`Failed to save assignment: ${err.message}`);
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>{{ modalTitle }}</template>

    <form class="space-y-4">
      <!-- Warning for non-editable days -->
      <div v-if="!canEdit" class="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
        <p class="text-sm text-yellow-800 dark:text-yellow-200">
          This day cannot be edited because the branch is closed.
        </p>
      </div>

      <!-- Staff & Branch Info (Calendar Day Edit Mode) -->
      <div v-if="calendarDay" class="p-3 bg-slate-50 dark:bg-slate-700 rounded-md">
        <div class="flex justify-between items-center">
          <div>
            <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
              {{ calendarStaffName }} @ {{ calendarBranchName }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{ calendarDay.date }} • {{ calendarDay.layer }} layer
            </p>
          </div>
          <div class="text-xs text-slate-500 dark:text-slate-400">
            <span v-if="isSpecificAssignment">Specific Assignment</span>
            <span v-else-if="isScheduleEdit">Schedule</span>
            <span v-else-if="isUnavailabilityEdit">Unavailability</span>
          </div>
        </div>
      </div>
      <div v-if="showScopeOptions" class="space-y-2">
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100">
          Edit Scope
        </label>
        <div class="space-y-2">
          <label class="flex items-center">
            <input
              v-model="editScope"
              type="radio"
              value="all"
              class="mr-2"
            />
            <span class="text-sm">
              Edit all days ({{ assignmentDateRange.start_date }} to {{ assignmentDateRange.end_date }})
            </span>
          </label>
          <label class="flex items-center">
            <input
              v-model="editScope"
              type="radio"
              value="selected"
              class="mr-2"
            />
            <span class="text-sm">Edit only selected days</span>
          </label>
        </div>
        <div v-if="editScope === 'all'" class="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> When editing all days, the date range is fixed. Only time slots can be modified.
        </div>
      </div>

      <!-- Edit Type Options (for schedule edits) -->
      <div v-if="showEditTypeOptions" class="space-y-2">
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100">
          Edit Type
        </label>
        <div class="space-y-2">
          <label class="flex items-center">
            <input
              v-model="editType"
              type="radio"
              value="one-time"
              class="mr-2"
            />
            <span class="text-sm">One-time change for this date only</span>
          </label>
          <label class="flex items-center">
            <input
              v-model="editType"
              type="radio"
              value="permanent"
              class="mr-2"
            />
            <span class="text-sm">Permanent change for all future occurrences</span>
          </label>
        </div>
        <div v-if="editType === 'permanent'" class="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> This will update the working hours for all future occurrences of this weekday in the schedule. Only one time slot is supported for permanent schedule edits.
        </div>
      </div>

      <!-- Branch -->
      <div v-if="!isEditMode">
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Branch <span class="text-red-500">*</span>
        </label>
        <select
          v-model="selectedBranch"
          class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option :value="null">Select a branch</option>
          <option v-for="branch in branchList" :key="branch.id" :value="branch.id">
            {{ branch.name }}
          </option>
        </select>
      </div>

      <!-- Staff -->
      <div v-if="!isEditMode">
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Staff <span class="text-red-500">*</span>
        </label>
        <select
          v-model="selectedStaff"
          class="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option :value="null">Select a staff member</option>
          <option v-for="option in staffOptions" :key="option.staff.id" :value="option.staff.id">
            {{ option.staff.name }}
          </option>
        </select>
      </div>

      <!-- Branch & Staff (Edit Mode Display) -->
      <div v-if="isEditMode && !calendarDay" class="space-y-4">
        <!-- Branch -->
        <div>
          <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            Branch
          </label>
          <div class="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
            {{ selectedBranchName || '-' }}
          </div>
        </div>

        <!-- Staff -->
        <div>
          <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
            Staff
          </label>
          <div class="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200">
            {{ selectedStaffName || '-' }}
          </div>
        </div>
      </div>

      <!-- Start Date -->
      <div>
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          Start Date <span class="text-red-500">*</span>
          <span v-if="isStartDateReadOnly" class="text-xs text-slate-500">(Read-only)</span>
        </label>
        <input
          v-model="startDate"
          type="date"
          :min="minDate"
          :max="maxDate"
          :readonly="isStartDateReadOnly"
          :class="[
            'w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-woot-500',
            isStartDateReadOnly 
              ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed'
              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
          ]"
        />
        <div v-if="minDate && maxDate && !isStartDateReadOnly" class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Must be between {{ minDate }} and {{ maxDate }}
        </div>
      </div>

      <!-- End Date -->
      <div>
        <label class="block text-sm font-medium text-slate-900 dark:text-slate-100 mb-2">
          End Date
          <span v-if="isEndDateReadOnly" class="text-xs text-slate-500">(Read-only)</span>
        </label>
        <input
          v-model="endDate"
          type="date"
          :min="minDate"
          :max="maxDate"
          :readonly="isEndDateReadOnly"
          :class="[
            'w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-woot-500',
            isEndDateReadOnly 
              ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed'
              : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800'
          ]"
        />
        <div v-if="minDate && maxDate && !isEndDateReadOnly" class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Must be between {{ minDate }} and {{ maxDate }}
        </div>
      </div>

      <!-- Time Slots -->
      <div v-if="!isUnavailabilityEdit || timeSlots.length > 0">
        <div class="flex items-center justify-between mb-2">
          <label class="block text-sm font-medium text-slate-900 dark:text-slate-100">
            Time Slots
            <span v-if="isScheduleEdit && editType === 'permanent'" class="text-xs text-slate-500">(Max 1 slot for permanent edits)</span>
          </label>
          <button
            type="button"
            @click="addTimeSlot"
            class="text-xs px-2 py-1 bg-woot-500 hover:bg-woot-600 text-white rounded"
            :disabled="!canEdit || (isScheduleEdit && editType === 'permanent' && timeSlots.length >= 1)"
          >
            + Add Slot
          </button>
        </div>

        <div v-if="timeSlots.length === 0" class="text-sm text-slate-500 dark:text-slate-400 py-2">
          No time slots added yet
        </div>

        <div v-for="(slot, index) in timeSlots" :key="index" class="flex gap-2 mb-2">
          <div class="flex-1">
            <label class="block text-xs text-slate-600 dark:text-slate-400 mb-1">Start Time</label>
            <input
              v-model="slot.startTime"
              type="time"
              class="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :disabled="!canEdit"
            />
          </div>
          <div class="flex-1">
            <label class="block text-xs text-slate-600 dark:text-slate-400 mb-1">End Time</label>
            <input
              v-model="slot.endTime"
              type="time"
              class="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500"
              :disabled="!canEdit"
            />
          </div>
          <button
            type="button"
            @click="removeTimeSlot(index)"
            class="self-end px-2 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            title="Remove slot"
            :disabled="!canEdit"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Unavailability Note -->
      <div v-if="isUnavailabilityEdit && timeSlots.length === 0" class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
        <p class="text-sm text-blue-800 dark:text-blue-200">
          This is an all-day unavailability. Add time slots to make it partial-day unavailability.
        </p>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button color-scheme="secondary" @click="$emit('close')" :disabled="isLoading">
          Cancel
        </woot-button>
        <woot-button @click="handleConfirm" :loading="isLoading" :disabled="isLoading || !canEdit">
          {{ isEditMode ? 'Update' : 'Create' }}
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>
